import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subject, Subscription, catchError, of, startWith, switchMap, tap } from 'rxjs';
import { Asociado } from '../../../api';
import { mensajeErrorCarga } from '../../core/http-errors';
import { AsociadosListadoService } from '../../services/asociados-listado.service';
import { CatalogosHistoricoService, OpcionAsociacion, OpcionCargo, OpcionEjercicio } from '../../services/catalogos-historico.service';
import { EstadoRelacion, HistoricoApiService, PaginaHistorico } from '../../services/historico-api.service';
import { PermisosService } from '../../services/permisos.service';
import { AlertService } from '../alert/alert.service';
import { AsociadoBuscadorComponent, mensajeErrorBusqueda } from '../asociado-buscador/asociado-buscador.component';
import { SpinnerComponent } from '../spinner/spinner.component';

import { HistoricoAsociado } from '../../../api';

/** Tipo conservado por compatibilidad: lo usan el formulario genérico y la ficha de asociado (agrupación por ejercicio del histórico legacy). */
import { PageHeaderComponent } from '../ui/page-header.component';

export interface HistoricoShow {
  [key: string]: any;
  ejercicio: number;
  historico: HistoricoAsociado[];
}

export const TAMANOS_HISTORICO = [10, 25, 50, 100];
export const TIPOS_REGISTRO = [{ id: 1, texto: 'Altas' }, { id: 2, texto: 'Cambios' }, { id: 3, texto: 'Bajas' }];
export const ESTADOS_FILTRO: { id: EstadoRelacion | 'todos'; texto: string }[] = [
  { id: 'todos', texto: 'Todos los estados' }, { id: 'vigente', texto: 'Vigentes' }, { id: 'historico', texto: 'Ejercicios anteriores' }, { id: 'baja', texto: 'Bajas' }, { id: 'desactivado', texto: 'Desactivados' }
];
export const ETIQUETA_ESTADO: Record<EstadoRelacion, { texto: string; clase: string; ayuda: string }> = {
  vigente: { texto: 'Vigente', clase: 'text-bg-success', ayuda: 'Relación en vigor en el ejercicio vigente.' },
  historico: { texto: 'Histórico', clase: 'text-bg-info', ayuda: 'Relación de un ejercicio cerrado (no es una baja).' },
  baja: { texto: 'Baja', clase: 'text-bg-secondary', ayuda: 'Baja lógica del registro.' },
  desactivado: { texto: 'Desactivado', clase: 'text-bg-warning', ayuda: 'Relación del ejercicio vigente desactivada sin baja (anomalía).' }
};

/** DNI/NIF comparable: sin espacios ni guiones y en mayúsculas (como normaliza la búsqueda de la API). */
export function normalizarNif(valor: unknown): string {
  return String(valor ?? '').replace(/[\s-]/g, '').toUpperCase();
}

const entero = (valor: string | null): number | null => {
  const n = Number(valor);
  return valor !== null && valor !== '' && Number.isInteger(n) && n > 0 ? n : null;
};

/**
 * Consulta del histórico de un asociado (P1.3 Wave 5). Se localiza al asociado bajo demanda (sin precargar asociados: DNI/NIF exacto
 * o búsqueda por nombre y apellidos) y sus relaciones se piden paginadas a GET /historico con filtros por ejercicio, asociación,
 * cargo, tipo de registro y estado, del ejercicio más reciente al más antiguo. El estado de la consulta vive en la URL
 * (?asociado=&ejercicio=&asociacion=&cargo=&tipo=&estado=&page=&size=&dir=): al volver de una ficha (o F5) reaparece tal como se dejó.
 */
@Component({
  selector: 'app-historico',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterLink, SpinnerComponent, AsociadoBuscadorComponent, PageHeaderComponent],
  templateUrl: './historico.component.html',
  styleUrl: './historico.component.scss'
})
export class HistoricoComponent implements OnInit, OnDestroy {
  readonly TAMANOS = TAMANOS_HISTORICO;
  readonly TIPOS = TIPOS_REGISTRO;
  readonly ESTADOS = ESTADOS_FILTRO;
  readonly ETIQUETA = ETIQUETA_ESTADO;

  user = new FormControl('');
  loadingData = false;
  asociadoSelected: Asociado | null = null;
  errorAsociado = '';

  // Filtros (todos opcionales)
  ejercicio: number | '' = '';
  asociacion: number | '' = '';
  cargo: number | '' = '';
  tipo: number | '' = '';
  estado: EstadoRelacion | 'todos' = 'todos';
  dir: 'asc' | 'desc' = 'desc';
  pagina = 1;
  pageSize = 25;

  ejercicios: OpcionEjercicio[] = [];
  asociaciones: OpcionAsociacion[] = [];
  cargos: OpcionCargo[] = [];

  cargando = false;
  error = '';
  resultado: PaginaHistorico | null = null;

  private readonly peticion$ = new Subject<void>();
  private readonly suscripciones = new Subscription();
  private consulta: Subscription | null = null;
  private catalogosPedidos = false;

  constructor(
    private readonly alertService: AlertService,
    private readonly listado: AsociadosListadoService,
    private readonly api: HistoricoApiService,
    private readonly catalogos: CatalogosHistoricoService,
    private readonly permisos: PermisosService,
    private readonly router: Router,
    private readonly route: ActivatedRoute
  ) {}

  get puedeEscribir(): boolean { return this.permisos.tieneGlobal('historico:escribir'); }
  get total(): number { return this.resultado?.paginacion.total ?? 0; }
  get totalPaginas(): number { return this.resultado?.paginacion.totalPages ?? 0; }
  get desde(): number { return this.total ? (this.pagina - 1) * this.pageSize + 1 : 0; }
  get hasta(): number { return this.total ? Math.min(this.total, this.desde + (this.resultado?.historico.length ?? 0) - 1) : 0; }
  get hayFiltros(): boolean { return this.ejercicio !== '' || this.asociacion !== '' || this.cargo !== '' || this.tipo !== '' || this.estado !== 'todos'; }

  ngOnInit(): void {
    this.restaurarEstado();
    this.suscripciones.add(
      this.peticion$.pipe(
        startWith(undefined),
        switchMap(() => {
          if (!this.asociadoSelected) { this.resultado = null; this.cargando = false; return of(null); }
          this.cargando = true; this.error = '';
          this.guardarEstado();
          return this.api.consultar({
            asociado: this.asociadoSelected.id, ejercicio: this.ejercicio || null, asociacion: this.asociacion || null, cargo: this.cargo || null,
            tipo: this.tipo || null, estado: this.estado, page: this.pagina, pageSize: this.pageSize, dir: this.dir
          }).pipe(catchError((err: HttpErrorResponse) => { this.error = mensajeErrorCarga(err, 'el histórico'); return of(null); }));
        }),
        tap(() => { this.cargando = false; })
      ).subscribe(res => {
        if (!res) { if (this.error) this.resultado = null; return; }
        // Si la página pedida ya no existe se vuelve a la última
        if (!res.historico.length && res.paginacion.total > 0 && this.pagina > res.paginacion.totalPages) {
          this.pagina = res.paginacion.totalPages;
          this.peticion$.next();
          return;
        }
        this.resultado = res;
      })
    );
    const id = entero(this.route.snapshot.queryParamMap.get('asociado'));
    if (id) this.recuperarAsociado(id);
  }

  ngOnDestroy(): void {
    this.consulta?.unsubscribe();
    this.suscripciones.unsubscribe();
  }

  private restaurarEstado(): void {
    const p = this.route.snapshot.queryParamMap;
    this.ejercicio = entero(p.get('ejercicio')) ?? '';
    this.asociacion = entero(p.get('asociacion')) ?? '';
    this.cargo = entero(p.get('cargo')) ?? '';
    const tipo = entero(p.get('tipo'));
    this.tipo = tipo && tipo <= 3 ? tipo : '';
    const estado = p.get('estado');
    this.estado = ESTADOS_FILTRO.some(e => e.id === estado) ? (estado as EstadoRelacion | 'todos') : 'todos';
    this.dir = p.get('dir') === 'asc' ? 'asc' : 'desc';
    const tamano = Number(p.get('size'));
    this.pageSize = (TAMANOS_HISTORICO as number[]).includes(tamano) ? tamano : 25;
    this.pagina = entero(p.get('page')) ?? 1;
  }

  private guardarEstado(): void {
    this.router.navigate([], {
      relativeTo: this.route, replaceUrl: true, queryParamsHandling: 'merge',
      queryParams: {
        asociado: this.asociadoSelected?.id ?? null,
        ejercicio: this.ejercicio === '' ? null : this.ejercicio, asociacion: this.asociacion === '' ? null : this.asociacion,
        cargo: this.cargo === '' ? null : this.cargo, tipo: this.tipo === '' ? null : this.tipo,
        estado: this.estado === 'todos' ? null : this.estado, dir: this.dir === 'asc' ? 'asc' : null,
        page: this.pagina > 1 ? this.pagina : null, size: this.pageSize !== 25 ? this.pageSize : null
      }
    });
  }

  private recuperarAsociado(id: number): void {
    this.loadingData = true;
    this.suscripciones.add(this.listado.obtenerPorId(id).subscribe({
      next: res => {
        this.loadingData = false;
        const encontrado = res?.asociados?.[0];
        if (encontrado) { this.fijarAsociado(encontrado, false); } else { this.errorAsociado = 'No se ha encontrado el asociado.'; }
      },
      error: (err: HttpErrorResponse) => { this.loadingData = false; this.errorAsociado = mensajeErrorCarga(err, 'el asociado'); }
    }));
  }

  /** Botón/Enter: búsqueda EXACTA por DNI/NIF (consulta remota acotada, sin lista completa). */
  async loadDataUser() {
    const texto = (this.user.value ?? '').trim();
    if (!texto) {
      this.alertService.warning('No ha seleccionado ningún dato de búsqueda', 5000);
      this.loadingData = false;
      return;
    }
    this.consulta?.unsubscribe();
    this.errorAsociado = '';
    this.loadingData = true;
    this.consulta = this.listado.buscar({ page: 1, pageSize: 25, nif: texto }).subscribe({
      next: pagina => {
        const exactos = (pagina?.asociados ?? []).filter(a => normalizarNif(a.nif) === normalizarNif(texto));
        this.loadingData = false;
        if (exactos.length === 1) {
          this.fijarAsociado(exactos[0], true);
        } else if (exactos.length === 0) {
          this.alertService.danger('No existe ningún asociado que coincida con el DNI - ' + texto, 5000);
        } else {
          this.alertService.danger('Hay varios asociados con ese DNI - ' + texto + '. Elige uno en la búsqueda.', 5000);
        }
      },
      error: (err: HttpErrorResponse) => {
        this.alertService.danger('Se produjo un error: ' + mensajeErrorBusqueda(err), 5000);
        this.loadingData = false;
      }
    });
  }

  /** Asociado elegido en la búsqueda por nombre, apellidos o DNI/NIF. */
  seleccionar(asociado: Asociado) {
    this.consulta?.unsubscribe();
    this.fijarAsociado(asociado, true);
  }

  private fijarAsociado(asociado: Asociado, reiniciar: boolean): void {
    this.asociadoSelected = asociado;
    if (reiniciar) { this.ejercicio = ''; this.asociacion = ''; this.cargo = ''; this.tipo = ''; this.estado = 'todos'; this.pagina = 1; }
    this.cargarCatalogos();
    this.peticion$.next();
  }

  /** Solo cuando hay un asociado elegido: ejercicios, asociaciones y cargos para los filtros (no se descarga ningún asociado). */
  private cargarCatalogos(): void {
    if (this.catalogosPedidos) return;
    this.catalogosPedidos = true;
    this.suscripciones.add(this.catalogos.ejerciciosRecientes().subscribe({ next: e => { this.ejercicios = e; }, error: () => { this.catalogosPedidos = false; } }));
    this.suscripciones.add(this.catalogos.asociacionesOrdenadas().subscribe({ next: a => { this.asociaciones = a; }, error: () => { this.catalogosPedidos = false; } }));
    this.suscripciones.add(this.catalogos.cargosOrdenados().subscribe({ next: c => { this.cargos = c; }, error: () => { this.catalogosPedidos = false; } }));
  }

  cambiarAsociado(): void {
    this.asociadoSelected = null;
    this.resultado = null;
    this.error = '';
    this.user.setValue('');
    this.router.navigate([], { relativeTo: this.route, replaceUrl: true, queryParams: {} });
  }

  /** Cambio de filtro o de tamaño: vuelve a la primera página. */
  reiniciar(): void { this.pagina = 1; this.peticion$.next(); }

  alternarOrden(): void { this.dir = this.dir === 'desc' ? 'asc' : 'desc'; this.reiniciar(); }

  irA(pagina: number): void {
    const total = this.resultado?.paginacion.totalPages ?? 1;
    this.pagina = Math.min(Math.max(1, pagina), Math.max(1, total));
    this.peticion$.next();
  }

  reintentar(): void { this.peticion$.next(); }

  limpiarFiltros(): void {
    this.ejercicio = ''; this.asociacion = ''; this.cargo = ''; this.tipo = ''; this.estado = 'todos';
    this.reiniciar();
  }

  nombreCompleto(a: Asociado): string { return [a.nombre, a.apellidos].filter(Boolean).join(' '); }
}
